/**
 * Fatiado de pages/Menu.tsx por responsabilidade (#131, R3).
 */
import { useState } from 'react'
import type { useQueryClient } from '@tanstack/react-query'
import { useMutation } from '@tanstack/react-query'
import { products as prodApi, type Product, type ProductIngredient } from '../../lib/api.js'
import { Button, Input, Switch, toast } from '../../components/ui/index.js'
import { cn } from '../../lib/utils.js'
import { useI18n } from '../../lib/i18n.js'
// ─── Ingredients editor ───────────────────────────────────────────────────────

export function IngredientsEditor({
  product,
  qc,
  onProductUpdate,
}: {
  product: Product
  qc: ReturnType<typeof useQueryClient>
  onProductUpdate: (p: Product) => void
}) {
  const { t } = useI18n()
  const [newName, setNewName] = useState('')
  const [removable, setRemovable] = useState(true)
  const refresh = async () => {
    try {
      onProductUpdate(await prodApi.get(product.id))
    } catch {
      /* lista já invalidada; o editor recarrega na próxima abertura */
    }
  }

  // #325: ingredientes prováveis da IA — aceitar vira ingrediente; descartar some
  const acceptMut = useMutation({
    mutationFn: (sid: string) => prodApi.acceptIngredientSuggestion(product.id, sid),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      if (data) onProductUpdate(data)
    },
    onError: (e: Error) => toast.error(e.message),
  })
  const dismissMut = useMutation({
    mutationFn: (sid: string) => prodApi.dismissIngredientSuggestion(product.id, sid),
    onSuccess: (_, sid) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({
        ...product,
        ingredient_suggestions: (product.ingredient_suggestions ?? []).filter((x) => x.id !== sid),
      })
    },
    onError: (e: Error) => toast.error(e.message),
  })
  const suggestions = product.ingredient_suggestions ?? []

  const addMut = useMutation({
    mutationFn: () =>
      prodApi.addIngredient(product.id, {
        name: newName.trim(),
        is_removable: removable,
        sort_order: product.ingredients.length,
      }),
    onSuccess: (ing) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      setNewName('')
      onProductUpdate({ ...product, ingredients: [...product.ingredients, ing] })
      // #325: mudar a composição reabre os alergénios (estado vem do servidor)
      void refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const delMut = useMutation({
    mutationFn: (id: string) => prodApi.deleteIngredient(product.id, id),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({ ...product, ingredients: product.ingredients.filter((i) => i.id !== id) })
      void refresh()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  // #333: alterna "Removível" na linha, sem apagar e recriar o ingrediente.
  const toggleRemovableMut = useMutation({
    mutationFn: (ing: ProductIngredient) =>
      prodApi.updateIngredient(product.id, ing.id, { is_removable: !ing.is_removable }),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      onProductUpdate({
        ...product,
        ingredients: product.ingredients.map((i) => (i.id === updated.id ? updated : i)),
      })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div className="rounded-xl border border-gray-200 p-4">
      <p className="text-sm font-medium text-gray-700 mb-3">{t('admin.menu.ingredients')}</p>
      <div className="space-y-2 mb-3">
        {product.ingredients.length === 0 && (
          <p className="text-xs text-gray-400">{t('admin.menu.no_ingredients')}</p>
        )}
        {product.ingredients.map((ing) => (
          <div
            key={ing.id}
            className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
          >
            <span className="text-sm min-w-0 break-words">{ing.name}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`catalog-ingredient-removable-${ing.id}`}
                aria-pressed={ing.is_removable}
                aria-label={t('admin.menu.toggle_removable_aria', { name: ing.name })}
                onClick={() => toggleRemovableMut.mutate(ing)}
                disabled={toggleRemovableMut.isPending}
                className={cn(
                  'rounded-full px-2 py-0.5 text-xs border transition-colors',
                  ing.is_removable
                    ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
                    : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200',
                )}
              >
                {ing.is_removable ? t('admin.menu.removable') : t('admin.menu.fixed')}
              </button>
              <button
                data-testid={`catalog-ingredient-delete-${ing.id}`}
                onClick={() => delMut.mutate(ing.id)}
                className="text-gray-400 hover:text-red-500 text-xs"
              >
                ✕
              </button>
            </div>
          </div>
        ))}
      </div>
      {suggestions.length > 0 && (
        <div
          className="mb-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-3"
          data-testid="catalog-ingredient-suggestions"
        >
          <p className="text-xs font-medium text-amber-800">
            ✦ {t('admin.menu.ingredient_suggestions')}
          </p>
          <p className="text-[11px] text-amber-700 mb-2">
            {t('admin.menu.ingredient_suggestions_hint')}
          </p>
          <div className="space-y-1">
            {suggestions.map((sug) => (
              <div
                key={sug.id}
                data-testid={`catalog-ingredient-suggestion-${sug.name}`}
                className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5"
              >
                <span className="text-sm text-gray-700">{sug.name}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    data-testid={`catalog-ingredient-suggestion-accept-${sug.name}`}
                    disabled={acceptMut.isPending}
                    onClick={() => acceptMut.mutate(sug.id)}
                    className="text-xs text-emerald-700 hover:underline"
                  >
                    ✓ {t('admin.menu.ingredient_accept')}
                  </button>
                  <button
                    type="button"
                    data-testid={`catalog-ingredient-suggestion-dismiss-${sug.name}`}
                    disabled={dismissMut.isPending}
                    onClick={() => dismissMut.mutate(sug.id)}
                    className="text-xs text-gray-400 hover:text-red-500"
                  >
                    ✕ {t('admin.menu.ingredient_dismiss')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          placeholder={t('admin.menu.ingredient_ph')}
          data-testid="catalog-ingredient-new-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          className="flex-1"
          onKeyDown={(e) => e.key === 'Enter' && newName.trim() && addMut.mutate()}
        />
        <Switch checked={removable} onChange={setRemovable} label={t('admin.menu.removable')} />
        <Button
          size="sm"
          variant="secondary"
          data-testid="catalog-ingredient-add"
          loading={addMut.isPending}
          onClick={() => addMut.mutate()}
          disabled={!newName.trim()}
        >
          +
        </Button>
      </div>
    </div>
  )
}
